// Demo console API.
//
// Shape-compatible with `createConsoleApi`, but it never performs a network
// request and never carries a bearer token. Read methods resolve with sample
// payloads. The agent-connection add/remove/test calls simulate a queued
// worker job (matching one already shown as pending in the sample data) so
// the demo page can show its real polling and pending-card behaviour; Test
// and Remove on one of the agent's own sign-ins (kind "mcp") are answered on
// the spot with no job, as the real BFF answers them. Every other mutation is
// rejected with an explicit demo-view message so no simulated success can be
// mistaken for live behaviour.

import { ConsoleApiError } from "@/api/consoleClient";
import {
  DEMO_CLIENT,
  DEMO_CLIENT_SLUG,
  DEMO_MEMBER,
  DEMO_MEMBERSHIPS,
  buildDemoAgentConnections,
  buildDemoAgentProfile,
  buildDemoConnections,
  buildDemoSkills,
} from "@/demo/demoData";
import { buildDemoGrowth } from "@/demo/demoGrowth";
import { buildDemoAlloyPeople, buildDemoAlloySlackPeople, buildDemoConversationHistory, buildDemoConversationPage } from "@/demo/demoUsage";

const READ_LATENCY_MS = 260;
const DEMO_AGENT_NAME = "Piper";
const PIPEDREAM_DEMO_APPS = new Set(["google_analytics", "google_sheets", "gmail", "amazon_seller", "canva", "pinterest"]);
const MCP_DEMO_APPS = new Set(["hubspot", "klaviyo", "notion", "asana", "figma", "stripe"]);
const KEY_DEMO_FIELDS = {
  trustpilot: [{ name: "TRUSTPILOT_API_KEY", label: "API key", secret: true }],
  vercel: [{ envKey: "VERCEL_API_TOKEN", label: "API token", secret: true, required: true }],
  monday: [{ envKey: "MONDAY_API_TOKEN", label: "API token", secret: true, required: true }],
  loop: [{ envKey: "LOOP_SUBSCRIPTIONS_API_TOKEN", label: "API token", secret: true, required: true }],
};
// The hosted sign-ins the dashboard also adds by key: the key form is the session's other way.
const MCP_KEY_DEMO_FIELDS = {
  figma: [{ envKey: "FIGMA_PERSONAL_ACCESS_TOKEN", label: "Personal access token", placeholder: "figd_...", secret: true, required: true }],
  klaviyo: [{ envKey: "KLAVIYO_PRIVATE_API_KEY", label: "Private API key", placeholder: "pk_...", secret: true, required: true }],
  asana: [{ envKey: "ASANA_ACCESS_TOKEN", label: "Personal access token", secret: true, required: true }],
};
// The apps the agent can also sign in to itself, in its own browser, beside the dashboard's own way.
const AGENT_BROWSER_DEMO_LABELS = { canva: "Canva", monday: "monday.com", vercel: "Vercel" };
// The flows the page keeps, with the chat beside them (method "guided"): Shopify's form, Meta's Facebook sign-in.
const GUIDED_DEMO_FLOWS = { shopify: "shopify_partner", meta_ads: "meta_signin" };

// What the server hands a seeded session: its first way (a sign-in link minted for the app, the key fields the bubble
// asks for, or for an app the demo knows nothing of, one key field under a name the person can change) and its other
// ways, as the real BFF answers them (connectSeedEntry).
function demoSeed(seed) {
  const app = seed?.app;
  if (!app) return null;
  const agentBrowser = AGENT_BROWSER_DEMO_LABELS[app] ? [{ kind: "agent_browser", label: `Your agent signs in to ${AGENT_BROWSER_DEMO_LABELS[app]} itself, in its own browser` }] : [];
  if (GUIDED_DEMO_FLOWS[app]) return { app, flow: GUIDED_DEMO_FLOWS[app], link: null, linkKind: null, flowId: null, fields: [], note: null, verified: null, method: "guided", alternatives: [] };
  if (MCP_DEMO_APPS.has(app)) {
    mcpPolls[app] = 0;
    const keyFields = MCP_KEY_DEMO_FIELDS[app];
    return { app, flow: "mcp", link: `https://example.com/sign-in?growthos-demo=${encodeURIComponent(app)}`, linkKind: "popup", flowId: `demo-flow-${app}`, fields: [], note: null, verified: true, method: "mcp", alternatives: keyFields ? [{ kind: "key", label: "Add a key", fields: keyFields }] : [] };
  }
  if (PIPEDREAM_DEMO_APPS.has(app)) pipedreamReads[app] = 0;
  if (PIPEDREAM_DEMO_APPS.has(app) || app === "higgsfield") return { app, flow: app === "higgsfield" ? "higgsfield" : "pipedream", link: `https://example.com/link?growthos-demo=${encodeURIComponent(app)}`, linkKind: "popup", flowId: null, fields: [], note: null, verified: true, method: app === "higgsfield" ? "higgsfield" : "pipedream", alternatives: agentBrowser };
  if (KEY_DEMO_FIELDS[app]) return { app, flow: "key", link: null, linkKind: null, flowId: null, fields: KEY_DEMO_FIELDS[app], note: null, verified: null, method: "key", alternatives: agentBrowser };
  const envKey = `${String(app).toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_+|_+$/g, "")}_API_KEY`;
  return { app, flow: "key", link: null, linkKind: null, flowId: null, fields: [{ envKey, label: "API key", placeholder: "", secret: true, required: true, ask: "required" }], note: null, verified: null, method: "custom", alternatives: [] };
}
const mcpPolls = {};
// A Pipedream link minted for the bubble lands on the third read of the app's status: the bubble's read as the link
// opens, then two checks, the way the native sign-in is approved after two polls.
const pipedreamReads = {};
const QUEUED = { clientSlug: DEMO_CLIENT_SLUG, jobId: "demo-job", status: "queued" };

// One of the agent's own sign-ins in the sample data, or null.
function demoSignIn(app) {
  return buildDemoAgentConnections().connections.find((card) => card.app === app && card.kind === "mcp") || null;
}

// A demo turn, streamed as the SSE events a real turn sends, so the bubble shows its real states.
function demoTurnStream(message) {
  const lower = String(message).toLowerCase();
  const title = (name) => name[0].toUpperCase() + name.slice(1);
  const connecting = lower.match(/connecting ([a-z0-9 .-]+?) from the dashboard/i)?.[1];
  const signingIn = lower.match(/connecting ([a-z0-9 .-]+?) from the dashboard\. i'll sign in/i)?.[1];
  const forMe = lower.match(/connecting ([a-z0-9 .-]+?) from the dashboard\. it says you sign in for me/i)?.[1] || lower.match(/please sign in to ([a-z0-9 .-]+?) for me/i)?.[1];
  const custom = lower.match(/i want to connect ([a-z0-9 .-]+?)\. how do we do it/i)?.[1];
  const guided = lower.match(/connecting ([a-z0-9 .-]+?) with the form on the dashboard/i)?.[1];
  const text = lower.includes("form through the dashboard")
    ? "Thanks. X SCALE has it now and finishes the connection; it shows as connected here once I have it."
    : guided
    ? `Sure. Fill in the ${title(guided)} form on the page beside this chat, step by step, and I will tell you where each part comes from. Nothing you type there comes through this chat.`
    : lower.includes("installed through the dashboard")
    ? "Got it. I checked the key just now and it works. I can use it from here on."
    : lower.includes("sign-in is complete")
      ? "Checked: the sign-in works and I can see the account. I can use it from here on."
      : forMe
        ? `Sure. Send me your ${title(forMe)} login here, and any code ${title(forMe)} emails you, and I will sign in from my own browser. I never keep the password.`
        : signingIn
        ? `Sure. Tap **Sign in to ${title(signingIn)}** beside this chat, approve it in the window that opens, and come back here. I will check the connection the moment it finishes.`
        : custom
        ? `Let's connect ${title(custom)}. The quickest way is an API key: open its settings, find **API keys**, create one with read access, and paste it into the key field below this chat (its name is filled in for you). If it has no keys, I can sign in to it myself instead.`
        : connecting
      ? `Sure. For ${connecting[0].toUpperCase() + connecting.slice(1)} you need a private API key:\n\n1. Open its settings and find **API** or **Integrations**.\n2. Create a key with read access.\n3. Paste it into the key field below this chat, not here.\n\nI will check it the moment it lands on my machine.`
      : `This is the demo workspace, so I cannot reach a real agent. In a live workspace ${DEMO_AGENT_NAME} answers here with your own numbers and tasks.`;
  const events = [
    "event: tool.started\ndata: {\"tool_name\":\"demo\"}\n\n",
    ...text.split(/(?<=\s)/).map((piece) => `event: assistant.delta\ndata: ${JSON.stringify({ delta: piece })}\n\n`),
    `event: assistant.completed\ndata: ${JSON.stringify({ content: text })}\n\n`,
    "event: run.completed\ndata: {\"usage\":{}}\n\n",
  ];
  const encoder = new TextEncoder();
  let index = 0;
  return {
    body: {
      getReader: () => ({
        read: () => new Promise((resolve) => setTimeout(() => resolve(index < events.length ? { done: false, value: encoder.encode(events[index++]) } : { done: true, value: undefined }), index === 0 ? 300 : 40)),
        releaseLock: () => {},
      }),
    },
  };
}

function resolveLater(factory, latency = READ_LATENCY_MS) {
  return new Promise((resolve) => {
    setTimeout(() => resolve(factory()), latency);
  });
}

function demoBlocked(message) {
  return Promise.reject(
    new ConsoleApiError(message, { status: 0, code: "demo_view_read_only" })
  );
}

export function createDemoApi() {
  return {
    isDemo: true,
    getPublicConfig: () => resolveLater(() => ({ demo: true }), 0),
    getMe: () =>
      resolveLater(() => ({
        member: { ...DEMO_MEMBER },
        memberships: DEMO_MEMBERSHIPS.map((membership) => ({ ...membership })),
        client: { ...DEMO_CLIENT },
      })),
    getAgentProfile: () => resolveLater(buildDemoAgentProfile),
    // Demo view has no agent to reach. The bubble still opens so the design can be reviewed: it answers with a
    // canned, clearly-demo reply streamed the way a real turn is. The full studio stays unavailable.
    getAgentChat: () => resolveLater(() => ({ available: true, online: true, agentName: DEMO_AGENT_NAME, sessions: [] })),
    createAgentChatSession: (options = {}) => resolveLater(() => ({ session: { id: options?.seed ? `demo-connect-${options.seed.app}` : "demo-session", ...(options?.seed ? { seed: demoSeed(options.seed) } : {}) } }), options?.seed ? 900 : READ_LATENCY_MS),
    getAgentChatSession: () => demoBlocked("Chat with your agent is not available in demo view."),
    renameAgentChatSession: () => demoBlocked("Chat with your agent is not available in demo view."),
    archiveAgentChatSession: () => demoBlocked("Chat with your agent is not available in demo view."),
    streamAgentChatTurn: ({ message = "" } = {}) => resolveLater(() => demoTurnStream(message), 400),
    getSkills: () => resolveLater(buildDemoSkills),
    // The Test section reads the Aurora sample set; its mutations stay read-only in demo view like every other mutation.
    getGrowth: () => resolveLater(buildDemoGrowth),
    setGrowthGoals: () => demoBlocked("Demo view cannot change targets. Sign in to a real workspace to set goals."),
    updateGrowthInitiative: () => demoBlocked("Demo view cannot change the plan. Sign in to a real workspace to act on it."),
    addGrowthExperiment: () => demoBlocked("Demo view cannot add test ideas. Sign in to a real workspace to propose one."),
    answerGrowthQuestion: () => demoBlocked("Demo view cannot answer questions. Sign in to a real workspace to answer your agent."),
    skipGrowthQuestion: () => demoBlocked("Demo view cannot skip questions. Sign in to a real workspace to answer your agent."),
    getConnections: () => resolveLater(buildDemoConnections),
    getAgentConnections: () => resolveLater(buildDemoAgentConnections),
    addAgentConnection: () => resolveLater(() => ({ clientSlug: DEMO_CLIENT_SLUG, jobId: "demo-job", status: "queued" })),
    addCustomAgentConnection: () => resolveLater(() => ({ clientSlug: DEMO_CLIENT_SLUG, jobId: "demo-job", status: "queued" })),
    // The real BFF checks and removes a sign-in through the agent right away (jobId null), so nothing waits on a job.
    removeAgentConnection: (app) => resolveLater(() => (demoSignIn(app)
      ? { clientSlug: DEMO_CLIENT_SLUG, jobId: null, status: "done", result: { status: "not_connected" } }
      : { ...QUEUED })),
    testAgentConnection: (app) => resolveLater(() => {
      const card = demoSignIn(app);
      return card
        ? { clientSlug: DEMO_CLIENT_SLUG, jobId: null, status: "done", result: { status: card.status, reason: card.reason, toolCount: card.toolCount } }
        : { ...QUEUED };
    }),
    getAgentConnectionJob: () => resolveLater(() => ({
      clientSlug: DEMO_CLIENT_SLUG,
      job: {
        id: "demo-job",
        app: "shopify",
        action: "test",
        status: "done",
        error: null,
        createdAt: "2026-09-11T20:00:00Z",
        completedAt: "2026-09-11T20:00:05Z",
      },
    })),
    // The native sign-in: a link to open, then "approved" after two polls, the way Hermes answers on a real VM.
    connectMcp: (app) => resolveLater(() => { mcpPolls[app] = 0; return { clientSlug: DEMO_CLIENT_SLUG, flowId: `demo-flow-${app}`, authorizationUrl: `https://example.com/sign-in?growthos-demo=${encodeURIComponent(app)}`, status: "authorization_required" }; }),
    // The demo brand is hosted with no other brand, so it has no creative tools to share.
    getConnectionSharing: () => resolveLater(() => ({ clientSlug: DEMO_CLIENT_SLUG, partners: [] })),
    shareAgentConnection: () => demoBlocked("Demo view cannot share tools. Sign in to a real workspace to share a creative tool."),
    getMcpFlow: (flowId) => resolveLater(() => {
      const app = String(flowId).replace(/^demo-flow-/, "");
      mcpPolls[app] = (mcpPolls[app] || 0) + 1;
      return mcpPolls[app] >= 2
        ? { clientSlug: DEMO_CLIENT_SLUG, flowId, status: "approved", error: null, toolCount: 41 }
        : { clientSlug: DEMO_CLIENT_SLUG, flowId, status: "authorization_required", error: null, toolCount: null };
    }),
    getConnectionStatus: (appKey) => resolveLater(() => {
      const connections = buildDemoConnections();
      let provider = connections.providers.find((item) => item.key === appKey) || null;
      if (provider?.key === "amazon_seller") {
        provider = {
          ...provider,
          status: "connected_unverified",
          account: { label: "Demo Seller Account" },
          lifecycle: {
            ...provider.lifecycle,
            phase: "connected_unverified",
            connectionGeneration: 1,
            connected: true,
          },
          capabilities: { ...provider.capabilities, connect: false, reconnect: true },
          action: { action: "authorize", operation: "reconnect", canStart: true, blocker: null },
        };
      }
      if (appKey in pipedreamReads && (pipedreamReads[appKey] += 1) >= 3) {
        provider = {
          key: appKey,
          label: appKey,
          ...provider,
          status: "identity_verified",
          account: { label: provider?.account?.label || "Demo account" },
          lifecycle: { ...provider?.lifecycle, phase: "identity_verified", connected: true, identityVerified: true },
        };
      }
      return {
        schemaVersion: connections.schemaVersion,
        generatedAt: connections.generatedAt,
        client: connections.client,
        canManage: connections.canManage,
        provider,
      };
    }),
    // The demo has one brand, so the sidebar never offers All brands; the page still answers if someone opens /brands.
    getBrands: () => resolveLater(() => ({
      schemaVersion: 1,
      clientSlug: DEMO_CLIENT.clientSlug,
      generatedAt: new Date().toISOString(),
      brands: [{
        clientSlug: DEMO_CLIENT.clientSlug, clientName: DEMO_CLIENT.clientName, role: DEMO_CLIENT.role,
        agent: { name: DEMO_AGENT_NAME, online: true }, hosting: null,
        credits: { available: true, configured: true, keyReady: true, alloyState: "off", balanceUsd: 23.75, monthlyUsd: 4.5, tokensPerUsd: 5 },
        connections: { available: true, checksAvailable: true, total: 3, connected: 3, notWorking: 0, notTested: 0, problems: [] },
      }],
    })),
    getCredits: () => resolveLater(() => ({
      schemaVersion: "growthos.alloy-credits.v1",
      configured: true,
      keyReady: true,
      alloyState: "off",
      balanceUsd: 23.75,
      capUsd: 10,
      usageUsd: 1.25,
      usage: { dailyUsd: 0.4, weeklyUsd: 1.25, monthlyUsd: 4.5 },
      // Every brand reads Alloy tokens since 2026-11-02, the demo brand too: 5 to the dollar since 2026-11-03, from the
      // dollar fields above.
      tokens: { perUsd: 5, balance: 118.75, usage: { daily: 2, weekly: 6.25, monthly: 22.5 } },
      routing: {
        windowDays: 30,
        requests: 1180,
        tokens: 2400000,
        models: [
          { model: "openai/gpt-5.4", requests: 543, tokens: 1100000, requestPercent: 46, usageUsd: 12.8 },
          { model: "anthropic/claude-opus-5", requests: 496, tokens: 1000000, requestPercent: 42, usageUsd: 20.5 },
          { model: "google/gemini-3.8-flash", requests: 141, tokens: 300000, requestPercent: 12, usageUsd: 5.99 },
        ],
        mix: { premium: 42, standard: 46, fast: 12 },
        comparator: { label: "Claude Fable 5.1" },
        savedUsd: 22.1,
        savedPercent: 36,
      },
      cachedAt: new Date().toISOString(),
      autoRecharge: { enabled: true, packageCents: 5000, thresholdUsd: 15, lastError: null },
      packages: [
        { packageKey: "alloy_usd_2000", amountCents: 2000, label: "$20" },
        { packageKey: "alloy_usd_5000", amountCents: 5000, label: "$50" },
        { packageKey: "alloy_usd_20000", amountCents: 20000, label: "$200" },
      ],
      purchases: [
        { id: "demo-purchase-1", createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), kind: "credit_purchase", amountCents: 2000, status: "settled" },
      ],
      route: null,
      clientSlug: DEMO_CLIENT.clientSlug,
      role: DEMO_MEMBERSHIPS[0]?.role ?? "member",
    })),
    // Usage by person on the Alloy page, and the Sessions page's two inventories it is grouped from.
    getAlloyPeople: () => resolveLater(buildDemoAlloyPeople),
    getAlloySlackPeople: () => resolveLater(() => buildDemoAlloySlackPeople()),
    getAgentConversations: () => resolveLater(() => buildDemoConversationPage()),
    getAgentConversationHistory: () => resolveLater(() => buildDemoConversationHistory()),
    getTeam: () => resolveLater(() => ({
      schemaVersion: 2,
      clientSlug: DEMO_CLIENT.clientSlug,
      accessManagement: {
        status: "available",
        canManage: false,
        actorRole: DEMO_MEMBERSHIPS[0]?.role ?? "member",
        allowedRoles: [],
      },
      members: [
        {
          id: "demo-member",
          email: DEMO_MEMBER.email,
          role: DEMO_MEMBERSHIPS[0]?.role ?? "member",
          state: "active",
          source: "demo",
          identityBound: true,
          policyManaged: false,
          canEdit: false,
          canRemove: false,
          createdAt: null,
          updatedAt: null,
        },
      ],
      operatingRoster: [],
      generatedAt: new Date().toISOString(),
    })),
    addTeamMember: () => demoBlocked("Demo view cannot add workspace members. Sign in to manage a real team."),
    setTeamMemberRole: () => demoBlocked("Demo view cannot change workspace roles. Sign in to manage a real team."),
    revokeTeamMember: () => demoBlocked("Demo view cannot remove workspace access. Sign in to manage a real team."),
    createCreditsCheckout: () => demoBlocked("Demo view cannot create a payment. Sign in to buy Alloy tokens for a real workspace."),
    saveCreditsSettings: () => demoBlocked("Demo view cannot change auto-recharge. Sign in to manage a real workspace."),
    requestAlloyState: () => demoBlocked("Demo view cannot change GrowthOS Alloy. Sign in to manage a real workspace."),
    testConnection: () =>
      demoBlocked("Demo view cannot run a live connection check. Sign in to test a real connection."),
    // A Pipedream link for the apps that link an account; every other sign-in stays read-only in demo view.
    startConnection: (appKey) => (PIPEDREAM_DEMO_APPS.has(appKey)
      ? resolveLater(() => ({ clientSlug: DEMO_CLIENT_SLUG, appKey, connectLinkUrl: `https://example.com/link?growthos-demo=${encodeURIComponent(appKey)}` }))
      : demoBlocked("Demo view cannot start a real authorization. Sign in to connect an account.")),
    reconnectConnection: () =>
      demoBlocked("Demo view cannot start a real reauthorization. Sign in to reconnect an account."),
    disconnectConnection: () =>
      demoBlocked("Demo view cannot disconnect a live account. Sign in to manage a real connection."),
    completeHiggsfieldConnection: () =>
      demoBlocked("Demo view cannot complete a live Higgsfield approval. Sign in to manage a real connection."),
    createRequest: () =>
      demoBlocked("Demo view cannot create a durable request. Sign in to submit a real request."),
  };
}
